import { create } from "zustand";
import {
  countPackagesByEstado,
  fetchPaquetesLoteado,
  type PaqueteDto,
} from "../firebase/firestore/paquetes";
import type { QueryDocumentSnapshot } from "firebase/firestore";
import { useDateRangeStore } from "./useDateRangeStore";
import { useAgenciaStore } from "./useAgenciaStore";

type PaqueteStore = {
  countEnTransito: number;
  countEntregado: number;
  countListoRetiro: number;
  countRecibido: number;
  countTotal: number;
  countsByEstado: Record<string, number>;
  allPaquetes: PaqueteDto[];
  lastDoc?: QueryDocumentSnapshot;
  hasMore: boolean;
  isLoadingAll: boolean;
  fetchCounts: () => Promise<void>;
  fetchAllPaquetes: () => Promise<void>;
  fetchNextPage: () => Promise<void>;
  fetchAllRemaining: () => Promise<void>;
  resetPackages: () => void;
  updatePaqueteInStore: (id: string, updated: PaqueteDto) => void;
};

export const usePaqueteStore = create<PaqueteStore>((set, get) => ({
  allPaquetes: [],
  lastDoc: undefined,
  hasMore: true,
  isLoadingAll: false,
  countEnTransito: 0,
  countEntregado: 0,
  countListoRetiro: 0,
  countRecibido: 0,
  countTotal: 0,
  countsByEstado: {},

  fetchCounts: async () => {
    const { fechaInicio, fechaFin } = useDateRangeStore.getState();
    const { agencia } = useAgenciaStore.getState();
    
    // Obtener IDs de estados desde agencia
    const estadosIds = agencia?.estados?.map((e) => e.id) || [];
    
    const counts = await countPackagesByEstado(
      fechaInicio.startOf("day").toDate(),
      fechaFin.endOf("day").toDate(),
      estadosIds
    );
    
    // Extraer total y crear objeto dinámico
    const { total, ...estadoCounts } = counts;
    
    // Mantener compatibilidad con campos antiguos
    set({
      countEnTransito: counts.en_transito || 0,
      countEntregado: counts.entregado || 0,
      countListoRetiro: counts.listo_para_retirar || 0,
      countRecibido: counts.recibido || 0,
      countTotal: total,
      countsByEstado: estadoCounts as Record<string, number>,
    });
  },

  fetchAllPaquetes: async () => {
    const { fechaInicio, fechaFin } = useDateRangeStore.getState();
    
    set({ isLoadingAll: true });
    
    try {
      const allPaquetes: PaqueteDto[] = [];
      let currentLastDoc: QueryDocumentSnapshot | undefined = undefined;
      let hasMore = true;

      // Cargar todos los lotes
      while (hasMore) {
        const result: {
          paquetes: PaqueteDto[];
          lastDoc?: QueryDocumentSnapshot;
        } = await fetchPaquetesLoteado(
          currentLastDoc,
          fechaInicio.startOf("day").toDate(),
          fechaFin.endOf("day").toDate()
        );

        const { paquetes, lastDoc } = result;
        allPaquetes.push(...paquetes);
        currentLastDoc = lastDoc;
        hasMore = !!lastDoc;

        // Actualizar progresivamente para mostrar progreso
        set({
          allPaquetes: [...allPaquetes],
          lastDoc: currentLastDoc,
          hasMore: false, // Ya no hay más después de cargar todo
        });
      }
    } finally {
      set({ isLoadingAll: false });
    }
  },

  fetchNextPage: async () => {
    const { fechaInicio, fechaFin } = useDateRangeStore.getState();
    const { lastDoc: currentLastDoc, hasMore } = get();

    if (!hasMore) return;

    const { paquetes, lastDoc: newLastDoc } = await fetchPaquetesLoteado(
      currentLastDoc,
      fechaInicio.startOf("day").toDate(),
      fechaFin.endOf("day").toDate()
    );

    set((state) => ({
      allPaquetes: [...state.allPaquetes, ...paquetes],
      lastDoc: newLastDoc,
      hasMore: !!newLastDoc,
    }));
  },

  fetchAllRemaining: async () => {
    const { fechaInicio, fechaFin } = useDateRangeStore.getState();
    const { countTotal } = get();
    
    set({ isLoadingAll: true });

    try {
      let currentLastDoc = get().lastDoc;
      let hasMore = get().hasMore;
      const allPaquetes = [...get().allPaquetes];

      // Cargar todos los lotes restantes
      while (hasMore && allPaquetes.length < countTotal) {
        const { paquetes, lastDoc: newLastDoc } = await fetchPaquetesLoteado(
          currentLastDoc,
          fechaInicio.startOf("day").toDate(),
          fechaFin.endOf("day").toDate()
        );

        allPaquetes.push(...paquetes);
        currentLastDoc = newLastDoc;
        hasMore = !!newLastDoc;

        // Actualizar estado progresivamente para mostrar progreso
        set({
          allPaquetes: [...allPaquetes],
          lastDoc: currentLastDoc,
          hasMore,
        });
      }
    } finally {
      set({ isLoadingAll: false });
    }
  },

  resetPackages: () => {
    set({
      allPaquetes: [],
      lastDoc: undefined,
      hasMore: true,
      isLoadingAll: false,
    });
  },

  updatePaqueteInStore: (id: string, updated: PaqueteDto) => {
    set((state) => ({
      allPaquetes: state.allPaquetes.map((p) =>
        p.id === id ? updated : p
      ),
    }));
  },
}));
